import {doGet, doPost} from "./do-fetch"

/*
 * Data layer for achievement badges. Mirrors the util/mini.js pattern: thin wrappers over
 * do-fetch that return plain data and never throw at the caller for a bad response, so a
 * badge problem can never take the dashboard down with it.
 */

// what the server buckets streaks / early-bird / night-owl by
const clientTimeZone = () => {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone
	}
	catch (e) {
		return null
	}
}

/*
 * Full badge collection for a prepkit: every award plus earned state.
 * Returns null on failure -- callers render nothing rather than a broken panel.
 */
const fetchAchievements = async (prepKitId) => {
	try {
		const res = await doGet(`/prepkit/${prepKitId}/achievements`)
		if (!res.ok) {
			console.error(`fetchAchievements(${prepKitId}) failed: ${res.status}`)
			return null
		}
		return await res.json()
	}
	catch (err) {
		console.error('Error fetching achievements', err)
		return null
	}
}

/*
 * Runs server-side detection. Idempotent, so it is safe to call on dashboard load and after
 * each answered question.
 *
 * Returns {earnedCount, newlyEarned, unannounced} or null on failure.
 */
const evaluateAchievements = async (prepKitId) => {
	try {
		const body = JSON.stringify({timeZone: clientTimeZone()})
		const res = await doPost(`/prepkit/${prepKitId}/achievements/evaluate`, {body})
		if (!res.ok) {
			console.error(`evaluateAchievements(${prepKitId}) failed: ${res.status}`)
			return null
		}
		return await res.json()
	}
	catch (err) {
		console.error('Error evaluating achievements', err)
		return null
	}
}

// tells the server a badge has been announced, so it is shown exactly once
const markAchievementsSeen = async (prepKitId, tokens) => {
	if (!tokens || !tokens.length) return
	try {
		const body = JSON.stringify({tokens})
		await doPost(`/prepkit/${prepKitId}/achievements/seen`, {body})
	}
	catch (err) {
		console.error('Error marking achievements seen', err)
	}
}

/*
 * Groups a catalog into [{category, achievements}] using the server's category order, with
 * earned badges first inside each category and catalog order as the tiebreak. Badges that
 * cannot be earned yet (detectable: false) are left out.
 */
const groupByCategory = (data) => {
	if (!data || !data.achievements) return []

	const order = data.categoryOrder || []
	const groups = new Map()

	for (const category of order) groups.set(category, [])
	for (const a of data.achievements) {
		// nothing can award these yet; "coming soon" read as a promise of the feature itself
		if (a.detectable === false) continue
		if (!groups.has(a.category)) groups.set(a.category, [])
		groups.get(a.category).push(a)
	}

	return [...groups.entries()]
		.filter(([, list]) => list.length > 0)
		.map(([category, list]) => ({
			category,
			achievements: [...list].sort((x, y) => {
				if (x.earned !== y.earned) return x.earned ? -1 : 1
				return x.sortOrder - y.sortOrder
			}),
		}))
}

export {fetchAchievements, evaluateAchievements, markAchievementsSeen, groupByCategory, clientTimeZone}
